import useNotification from 'hooks/useNotification';
import { Button, Flex, Skeleton, Typography } from 'antd';
import { EmptyTab } from '../EmptyTab';
import { FilterNoticeBoard, NoticeBoardFilter } from './components/Filter';
import { HttpError, useList } from '@refinedev/core';
import {
  IProfessorAreaNoticeBoardPost,
  IProfessorAreaNoticeBoardShow
} from 'interfaces/professor_area/notice_boards';
import { ModalCreateNoticeBoard } from './components/ModalCreate';
import { NoticeBoardList } from './components/List';
import { PlusOutlined } from '@ant-design/icons';
import { useCallback, useMemo, useState } from 'react';
import { useCanMultiple } from 'components/layout/sider';
import { useModalForm } from '@refinedev/antd';
import { useParams } from 'react-router-dom';

export const ITEMS_PER_PAGE = 5;

export const TabNoticeBoard = () => {
  const params = useParams();
  const classroomId = params.id!;
  const [page, setPage] = useState(1);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filters, setFilters] = useState<FilterNoticeBoard>({
    by_priority: undefined,
    by_title: ''
  });
  const { showError } = useNotification();
  const [createPermission, showPermission, deletePermission] = useCanMultiple([
    { resource: 'professor_area/notice_boards', action: 'create' },
    { resource: 'professor_area/notice_boards', action: 'show' },
    { resource: 'professor_area/notice_boards', action: 'delete' }
  ]);
  const canCreateNoticeBoard = createPermission.data?.can ?? false;
  const canShowNoticeBoard = showPermission.data?.can ?? false;
  const canDeleteNoticeBoard = deletePermission.data?.can ?? false;

  const { data, refetch, isLoading } = useList<IProfessorAreaNoticeBoardShow>({
    resource: 'professor_area/notice_boards',
    filters: [
      { field: 'filter[by_classroom_id]', operator: 'eq', value: classroomId },
      { field: 'filter[by_title]', operator: 'eq', value: filters.by_title },
      {
        field: 'filter[by_priority]',
        operator: 'eq',
        value: filters.by_priority
      },
      { field: 'page', operator: 'eq', value: page },
      { field: 'items', operator: 'eq', value: ITEMS_PER_PAGE }
    ]
  });

  const handleClear = () => {
    setFilters({
      by_priority: undefined,
      by_title: ''
    });
    setPage(1);
  };

  const handleSearch = useCallback(
    async (formData: FilterNoticeBoard) => {
      try {
        setFilters(formData);
        setFilterLoading(true);
        setPage(1);
        await refetch();
      } catch (error) {
        showError('Não foi possivel filtrar os avisos. Tente novamente!');
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setFilterLoading(false);
      }
    },
    [refetch, showError]
  );

  const handleChangePage = useCallback(
    (page: number) => {
      setPage(page);
      refetch();
    },
    [refetch]
  );

  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createModalShow,
    close: createModalClose,
    formLoading
  } = useModalForm<
    IProfessorAreaNoticeBoardPost,
    HttpError,
    IProfessorAreaNoticeBoardPost
  >({
    resource: 'professor_area/notice_boards',
    action: 'create',
    redirect: false,
    autoResetForm: true,
    onMutationSuccess: () => {
      handleChangePage(1);
    },
    successNotification: () => ({
      type: 'success',
      message: 'Aviso cadastrado com sucesso',
      description: 'Sucesso!'
    }),
    errorNotification: () => ({
      type: 'error',
      message: 'Não foi possível cadastrar o aviso. Tente novamente!',
      description: 'Algo deu errado!'
    })
  });

  const hasData = useMemo(() => {
    return !isLoading && data?.data && data?.data?.length > 0;
  }, [data, isLoading]);

  if (isLoading) {
    return (
      <Skeleton active paragraph={{ rows: 6 }} className="w-full h-full" />
    );
  }

  const EmptyComponent = () => {
    return (
      <EmptyTab
        title="Nenhum aviso encontrado"
        buttonText={canCreateNoticeBoard ? 'Novo aviso' : undefined}
        onClick={canCreateNoticeBoard ? () => createModalShow() : undefined}
      />
    );
  };

  const ContentComponent = () => {
    return (
      <Flex vertical>
        <NoticeBoardFilter
          onClear={handleClear}
          onSearch={handleSearch}
          isLoading={filterLoading}
        />

        <Flex justify="space-between" align="center" className="mb-4">
          <Typography.Title level={5}>Avisos da turma</Typography.Title>
          {canCreateNoticeBoard && (
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => createModalShow()}
            >
              Novo aviso
            </Button>
          )}
        </Flex>

        <NoticeBoardList
          notices={data?.data}
          page={page}
          total={data?.total}
          isLoading={formLoading}
          itemsPerPage={ITEMS_PER_PAGE}
          onChangePage={handleChangePage}
          refetch={refetch}
          canShow={canShowNoticeBoard}
          canDelete={canDeleteNoticeBoard}
        />
      </Flex>
    );
  };

  return (
    <>
      {hasData ? <ContentComponent /> : <EmptyComponent />}

      {canCreateNoticeBoard && createModalProps.open && (
        <ModalCreateNoticeBoard
          modalProps={createModalProps}
          formProps={createFormProps}
          classroomId={classroomId}
          isLoading={formLoading}
          onCloseModal={createModalClose}
        />
      )}
    </>
  );
};
